// ./src/components/features/EventSetup/useEventPaidSales.ts
//
// What an event sells online that costs money (the API's `eventPaidSales`) — the one read behind the getting-started
// card-payments step (#161 b) and the test-mode banner's "connect Stripe first" (#184).
//
// ⚠ `settled` says the read for THIS event answered — OK or not. `paid` null with `settled` true is a FAILED read, which
//   is not "sells nothing": each caller decides what a failure means on its screen.
// ⓘ Kept on screen through a reload (no flicker), and only ever used for the event it was read for.
import { useEffect, useState } from 'react';
import { OP_RESULT_CODES } from '@sdflc/api-helpers';

import { EventPaidSales, EventService } from '@/utils/api';

export interface UseEventPaidSalesReturn {
  /** The answer for this event; null before it, or when the read failed. */
  paid: EventPaidSales | null;
  /** A read for this event has answered (or failed). */
  settled: boolean;
}

type Read = { eventId: string; paid: EventPaidSales | null };

/** `enabled` — read once it is true (a screen that has no use for the answer never asks); `reloadKey` reads again. */
export const useEventPaidSales = (eventId: string | null | undefined, enabled: boolean, reloadKey?: number): UseEventPaidSalesReturn => {
  const [read, setRead] = useState<Read | null>(null);

  useEffect(() => {
    if (!enabled || !eventId) {
      return;
    }

    let alive = true;

    void EventService.paidSales(eventId)
      .then((res) => {
        if (alive) {
          setRead({ eventId, paid: res.code === OP_RESULT_CODES.OK ? ((res.getDataFirst() as EventPaidSales | undefined) ?? null) : null });
        }
      })
      .catch(() => {
        if (alive) {
          setRead({ eventId, paid: null });
        }
      });

    return () => {
      alive = false;
    };
  }, [eventId, enabled, reloadKey]);

  const mine = !!eventId && read?.eventId === eventId;
  const paid = mine && read?.paid?.eventId === eventId ? (read?.paid ?? null) : null;

  return { paid, settled: mine };
};
